import React, { ReactNode, createContext, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { DraggableModalWrapper } from "../components/globalModal/GlobalModalWrapper";
import { useModalPosition } from "../hooks/use-modal-position";

export type GlobalModal<T = any> = {
  id: string;
  title: ReactNode;
  content: (params: T) => ReactNode;
  onClose: (id: string) => void;
  initialPosition?: { x: number; y: number };
  params?: T;
  width?: number | string;
  height?: number | string;
  zIndex?: number;
};

type DialogContextType = {
  dialogs: GlobalModal[];
  openModal: <T = any>(dialog: Omit<GlobalModal<T>, "zIndex">) => void;
  closeModal: (id: string) => void;
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

/**
 * max number of dialogs that can be open at a time
 */
const MAX_DIALOGS = 3;

/**
 * render section for global dialogs
 *
 * @author [Rajkumar Rathod]
 */
export const GlobalModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [dialogs, setDialogs] = useState<GlobalModal[]>([]);
  const { calculateCenterPosition } = useModalPosition();

  const openModal = useCallback((dialog: Omit<GlobalModal, "zIndex">) => {
    setDialogs((prev) => {
      /**
       * prevent duplicate dialogs opening
       */
      if (prev.find((d) => d.id === dialog.id)) return prev;
      /**
       * max dialog open attempted
       */
      if (prev.length >= MAX_DIALOGS) return prev;
      return [...prev, { ...dialog, zIndex: prev.length + 1 }];
    });
  }, []);

  const closeModal = useCallback((id: string) => {
    setDialogs((prev) => prev.filter((d) => d.id !== id));
  }, []);

  /**
   * bring modal to front when mouse over
   * if there is only one modal, don't bring to front
   */
  const bringToFront = useCallback((id: string) => {
    setDialogs((prev) => {
      const maxZ = prev?.reduce((max, d) => Math.max(max, d.zIndex ?? 0), 0);

      return prev.map((d) => (d.id === id ? { ...d, zIndex: maxZ + 2 } : d));
    });
  }, []);

  return (
    <DialogContext.Provider value={{ dialogs, openModal, closeModal }}>
      {children}

      {/* render section for global dialogs using portal */}
      {createPortal(
        dialogs.map((dialog, index, arr) => (
          <div
            key={dialog.id}
            onMouseOver={() => arr.length > 1 && bringToFront(dialog.id)}
          >
            <DraggableModalWrapper
              index={index}
              {...dialog}
              initialPosition={
                dialog.initialPosition ??
                calculateCenterPosition(dialog.width, dialog.height)
              }
              onClose={() => dialog.onClose(dialog.id)}
              zIndex={dialog.zIndex}
            />
          </div>
        )),
        document.body
      )}
    </DialogContext.Provider>
  );
};
